// API de GWT para Vue 3
// Migrado desde gwt.js
// Proporciona acceso tipado a las funciones GWT

import type {
  Configuration,
  Department,
  Project,
  SessionInformation,
  WorkPlace,
} from "@wkt/api/gwt/gwtDTO";

/**
 * API cliente para comunicación con GWT
 */
export class GWTApi {
  private getBridge() {
    return window.$gwtBridge || null;
  }

  /**
   * Obtiene la información de sesión del usuario actual
   */
  getSessionInformation(): SessionInformation | null {
    const bridge = this.getBridge();
    if (!bridge || typeof bridge.getSessionInformation !== "function") {
      return null;
    }

    try {
      const data = bridge.getSessionInformation();
      return data ? JSON.parse(data) : null;
    } catch (error) {
      console.error("Error parsing session information:", error);
      return null;
    }
  }

  /**
   * Obtiene todas las configuraciones de la aplicación
   */
  getConfigurations(): Configuration[] {
    const bridge = this.getBridge();
    if (!bridge || typeof bridge.getConfigurations !== "function") {
      return [];
    }

    try {
      const data = bridge.getConfigurations();
      return data ? JSON.parse(data) : [];
    } catch (error) {
      console.error("Error parsing configurations:", error);
      return [];
    }
  }

  /**
   * Obtiene todos los centros de trabajo
   */
  getWorkplaces(): WorkPlace[] {
    const bridge = this.getBridge();
    if (!bridge || typeof bridge.getWorkplaces !== "function") {
      return [];
    }

    try {
      const data = bridge.getWorkplaces();
      return data ? JSON.parse(data) : [];
    } catch (error) {
      console.error("Error parsing workplaces:", error);
      return [];
    }
  }

  /**
   * Obtiene todos los departamentos
   */
  getDepartments(): Department[] {
    const bridge = this.getBridge();
    if (!bridge || typeof bridge.getDepartments !== "function") {
      return [];
    }

    try {
      const data = bridge.getDepartments();
      return data ? JSON.parse(data) : [];
    } catch (error) {
      console.error("Error parsing departments:", error);
      return [];
    }
  }

  /**
   * Obtiene todos los proyectos
   */
  getProjects(): Project[] {
    const bridge = this.getBridge();
    if (!bridge || typeof bridge.getProjects !== "function") {
      return [];
    }

    try {
      const data = bridge.getProjects();
      return data ? JSON.parse(data) : [];
    } catch (error) {
      console.error("Error parsing projects:", error);
      return [];
    }
  }

  /**
   * Descarga un archivo desde el servidor
   */
  downloadFile(url: string, fileName: string): void {
    const bridge = this.getBridge();
    if (!bridge || typeof bridge.downloadFile !== "function") {
      return;
    }
    bridge.downloadFile(url, fileName);
  }

  /**
   * Verifica si la aplicación se está ejecutando en un dispositivo móvil
   */
  isApp(): boolean {
    const bridge = this.getBridge();
    if (!bridge || typeof bridge.isApp !== "function") {
      return false;
    }
    return bridge.isApp();
  }

  /**
   * Valida CIF/NIF según el país
   * @param countryId - ID del país (no usado en client, mantener '' por compatibilidad)
   * @param value - Valor a validar
   */
  validateCIF(countryId: string, value: string): boolean {
    const bridge = this.getBridge();
    if (!bridge || typeof bridge.validateCIF !== "function") {
      return false;
    }
    return bridge.validateCIF(countryId, value);
  }

  /**
   * Valida teléfono según el país
   * @param countryId - ID del país (no usado en client, mantener '' por compatibilidad)
   * @param value - Valor a validar
   */
  validatePhone(countryId: string, value: string): boolean {
    const bridge = this.getBridge();
    if (!bridge || typeof bridge.validatePhone !== "function") {
      return false;
    }
    return bridge.validatePhone(countryId, value);
  }

  /**
   * Valida código postal según el país
   * @param countryId - ID del país (no usado en client, mantener '' por compatibilidad)
   * @param value - Valor a validar
   */
  validatePostalCode(countryId: string, value: string): boolean {
    const bridge = this.getBridge();
    if (!bridge || typeof bridge.validatePostalCode !== "function") {
      return false;
    }
    return bridge.validatePostalCode(countryId, value);
  }

  /**
   * Valida separador decimal según locale
   * @param value - Valor a validar
   * @param locale - Locale (no usado en client, mantener '' por compatibilidad)
   */
  validateDecimalSeparator(value: string, locale: string): boolean {
    const bridge = this.getBridge();
    if (!bridge || typeof bridge.validateDecimalSeparator !== "function") {
      return false;
    }
    return bridge.validateDecimalSeparator(value, locale);
  }

  /**
   * Procesa eventos de widgets (para DialogManager)
   * @param mode - Modo del evento
   * @param type - Tipo de evento
   * @param action - Acción a realizar
   * @param payload - Datos del evento
   */
  processEvent(
    mode: string,
    type: string,
    action: string,
    payload: unknown
  ): void {
    const bridge = this.getBridge();
    if (bridge && typeof bridge.processEvent === "function") {
      bridge.processEvent(mode, type, action, payload);
    }
  }
}

// Exportar instancia única (singleton)
export const gwtApi = new GWTApi();

// Exportar como default para compatibilidad
export default gwtApi;
